import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Spinner,
  Text,
  Input,
  Select,
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Divider,
  useDisclosure,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import Pagination from '@olagg/ui-kit/Pagination';
import { AdminTreasureGameOpportunity, OlaggModule, PermissionEnum } from '@olagg/db-types';
import useTreasureGameOpportunities from './hooks/useTreasureGameOpportunities';
import useRoles from '../../../features/Roles/hooks/useRoles';
import { FaPlus } from 'react-icons/fa';
import { ROUTES } from '../../../consts/routes';

const OpportunityDetailsModal = ({ 
  isOpen, 
  onClose, 
  opportunity 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  opportunity: AdminTreasureGameOpportunity;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Detalles de la Oportunidad Extra</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Header con información básica */}
            <HStack spacing={4} align="start">
              <VStack align="start" flex="1">
                <Text fontSize="xl" fontWeight="bold">Oportunidad #{opportunity.id}</Text>
                <Badge colorScheme={opportunity.active ? "green" : "red"}>
                  {opportunity.active ? "Activa" : "Inactiva"}
                </Badge>
                <Badge colorScheme={opportunity.used ? "orange" : opportunity.expired ? "red" : "blue"}>
                  {opportunity.used ? "Usada" : opportunity.expired ? "Expirada" : "Disponible"}
                </Badge>
              </VStack>
            </HStack>

            <Divider />

            {/* Información del usuario */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={2}>Usuario</Text>
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <Text>ID:</Text>
                  <Text fontWeight="medium">{opportunity.userInfo.id}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>Nombre:</Text>
                  <Text fontWeight="medium">{opportunity.userInfo.name}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>Email:</Text>
                  <Text fontWeight="medium">{opportunity.userInfo.email}</Text>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Estado y disponibilidad */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={2}>Estado</Text>
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <Text>Disponible:</Text>
                  <Badge colorScheme={opportunity.available ? "green" : "red"}>
                    {opportunity.available ? "Sí" : "No"}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text>Usada:</Text>
                  <Badge colorScheme={opportunity.used ? "orange" : "green"}>
                    {opportunity.used ? "Sí" : "No"}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text>Expirada:</Text>
                  <Badge colorScheme={opportunity.expired ? "red" : "green"}>
                    {opportunity.expired ? "Sí" : "No"}
                  </Badge>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Fechas */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={2}>Fechas</Text>
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <Text>Creada:</Text>
                  <Text fontWeight="medium">{moment(opportunity.createdAt).format('DD/MM/YYYY HH:mm')}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>Actualizada:</Text>
                  <Text fontWeight="medium">{moment(opportunity.updatedAt).format('DD/MM/YYYY HH:mm')}</Text>
                </HStack>
                {opportunity.usedAt && (
                  <HStack justify="space-between">
                    <Text>Usada:</Text>
                    <Text fontWeight="medium">{moment(opportunity.usedAt).format('DD/MM/YYYY HH:mm')}</Text>
                  </HStack>
                )}
                {opportunity.expiresAt && (
                  <HStack justify="space-between">
                    <Text>Expira:</Text>
                    <Text fontWeight="medium">{moment(opportunity.expiresAt).format('DD/MM/YYYY HH:mm')}</Text>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Notas */}
            {opportunity.notes && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="md" fontWeight="semibold" mb={2}>Notas</Text>
                  <Text>{opportunity.notes}</Text>
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const Actions = ({ opportunity, onRefresh }: { 
  opportunity: AdminTreasureGameOpportunity;
  onRefresh: () => void;
}) => {
  const navigate = useNavigate();
  const { missingPermission } = useRoles();
  const { deleteTreasureOpportunity } = useTreasureGameOpportunities();
  const [isDeleting, setIsDeleting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleEditOpportunity = () => {
    navigate(`/mini_games/treasure-opportunities/${opportunity.id}`);
  };

  const handleRemoveOpportunity = async () => {
    if (window.confirm('¿Estás seguro que deseas eliminar esta oportunidad extra?')) {
      setIsDeleting(true);
      try {
        await deleteTreasureOpportunity(opportunity.id.toString());
        console.log("Oportunidad eliminada");
        onRefresh(); // Refrescar la lista después de eliminar
      } catch (error) {
        console.error("Error eliminando oportunidad:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  }

  const canEdit = !missingPermission(OlaggModule.TreasuresGame, PermissionEnum.EDIT);
  const canDelete = !missingPermission(OlaggModule.TreasuresGame, PermissionEnum.DELETE);

  return (
    <>
      <Box display="flex" gap="10px" flexWrap="wrap">
        <Button 
          colorScheme="blue"
          size="sm"
          onClick={onOpen}
        >
          Detalles
        </Button>
        <Button 
          disabled={!canEdit}
          colorScheme="green"
          size="sm"
          onClick={handleEditOpportunity}
        >
          Editar
        </Button>
        <Button 
          disabled={!canDelete || isDeleting || opportunity.used} 
          colorScheme="red"
          size="sm"
          onClick={handleRemoveOpportunity}
          isLoading={isDeleting}
          loadingText="Eliminando..."
        >
          Eliminar
        </Button>
      </Box>
      
      <OpportunityDetailsModal 
        isOpen={isOpen} 
        onClose={onClose} 
        opportunity={opportunity} 
      />
    </>
  )
}

const TreasureGameOpportunities = () => {
  const navigate = useNavigate();
  const { missingPermission } = useRoles();
  const [searchParams, setSearchParams] = useSearchParams()
  const { getTreasureOpportunities, loading } = useTreasureGameOpportunities();
  const [opportunities, setOpportunities] = useState<AdminTreasureGameOpportunity[]>([]);
  const [count, setCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filter, setFilter] = useState({
    status: searchParams.get('status') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10),
  })
  const { page, limit, status } = filter

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    const parsedValue = (name === 'page' || name === 'limit') ? Number(value) : value;
    setFilter({ ...filter, [name]: parsedValue })

    if (value) searchParams.set(name, value)
    else searchParams.delete(name)
    setSearchParams(searchParams)
  }

  const onClearFilters = () => {
    setFilter({
      status: '',
      page: 1,
      limit: 10,
    });
    setSearchParams(new URLSearchParams());
  }

  const refreshOpportunities = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchOpportunities = async () => {
    try {
      const response = await getTreasureOpportunities({ 
        limit, 
        page, 
        status: status as 'active' | 'expired' | 'used' | undefined,
      });
      setOpportunities(response.treasureOpportunities);
      setCount(response.meta.count);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, [page, limit, status, refreshTrigger])

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlStatus = searchParams.get('status');

    if (urlPage || urlLimit || urlStatus) {
      setFilter({
        status: urlStatus || '',
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 10),
      });
    }
  }, []);

  const getStatusBadge = (opportunity: AdminTreasureGameOpportunity) => {
    if (opportunity.used) {
      return <Badge colorScheme="orange">Usada</Badge>;
    }
    if (opportunity.expired) {
      return <Badge colorScheme="red">Expirada</Badge>;
    }
    if (opportunity.active && opportunity.available) {
      return <Badge colorScheme="green">Activa</Badge>;
    }
    return <Badge colorScheme="gray">Inactiva</Badge>;
  };

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Oportunidades Extra de Treasure Game
        </Heading>

        <Flex w='full' alignItems='center' gap='5'>
          <Button
            mt='2' mb='5'
            display='flex'
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={() => navigate(ROUTES.MINI_GAMES.treasure_opportunities.create)}
            isDisabled={missingPermission(OlaggModule.TreasuresGame, PermissionEnum.CREATE)}
          >
            Agregar Oportunidad Extra
          </Button>
        </Flex>

        {/* Inline filter */}
        <Box
          mb={6}
          p={4}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.600"
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
          gap={4}
        >
          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Estado
            </Text>
            <Select
              placeholder="Todos los estados"
              value={status}
              onChange={(e) => onFilter({ name: 'status', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            >
              <option value="active">Activas</option>
              <option value="expired">Expiradas</option>
              <option value="used">Usadas</option>
            </Select>
          </Box>

          <Box>
            <Text color="transparent" fontSize="sm" mb={1}>
              .
            </Text>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={onClearFilters}
              size="sm"
              w="full"
            >
              Limpiar filtros
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          opportunities?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable
                    variant="striped"
                    size="sm"
                    colorScheme="olaggPurple"
                    fontSize="18px"
                    position='relative'
                    whiteSpace={'normal'}
                  >
                    <Thead>
                      <Tr>
                        <Th color="white">ID</Th>
                        <Th color="white">Usuario</Th>
                        <Th color="white">Email</Th>
                        <Th color="white">Estado</Th>
                        <Th color="white">Creada</Th>
                        <Th color="white">Expira</Th>
                        <Th color="white">Usada</Th>
                        <Th color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {opportunities.map((opportunity) => (
                        <Tr key={opportunity.id}>
                          <Td color="white">{opportunity.id}</Td>
                          <Td color="white">{opportunity.userInfo.name}</Td>
                          <Td color="white">{opportunity.userInfo.email}</Td>
                          <Td color="white">{getStatusBadge(opportunity)}</Td>
                          <Td color="white">
                            {moment(opportunity.createdAt).format('DD/MM/YYYY HH:mm')}
                          </Td>
                          <Td color="white">
                            {opportunity.expiresAt 
                              ? moment(opportunity.expiresAt).format('DD/MM/YYYY HH:mm')
                              : 'Sin expiración'
                            }
                          </Td>
                          <Td color="white">
                            {opportunity.usedAt 
                              ? moment(opportunity.usedAt).format('DD/MM/YYYY HH:mm')
                              : '-'
                            }
                          </Td>
                          <Td color="white">
                            <Actions opportunity={opportunity} onRefresh={refreshOpportunities} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay oportunidades cargadas</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={(page: number) =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={(val: number) =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />
      </Box>
    </Center>
  )
}

export default TreasureGameOpportunities;